import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  CheckCircle, XCircle, CreditCard, Clock, 
  User, AlertCircle, Loader2, Calendar, Phone, Mail, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/common/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Pagina Check-in Paziente
 * 
 * Flusso:
 * 1. Paziente inquadra QR code fisso in reception
 * 2. Se non loggato: redirect al login con return_url
 * 3. Se loggato: mostra "Conferma Arrivo"
 * 4. Dopo conferma: scelta pagamento (carta online o contanti)
 *    - Carta: link pagamento Stripe, dopo pagamento notifica admin
 *    - Contanti: notifica admin per check-in manuale
 * 5. Dopo check-in: paziente in sala d'attesa, SMS quando chiamato
 */
const CheckIn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Step: 'loading' | 'login_required' | 'confirm_arrival' | 'payment_choice' | 'payment_sent' | 'waiting_cash' | 'success' | 'error'
  const [step, setStep] = useState('loading');
  const [loading, setLoading] = useState(false);
  
  // User state
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Form state (per utenti non loggati o per ricerca appuntamento)
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: ''
  });
  
  // Result state
  const [checkInResult, setCheckInResult] = useState(null);
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null); // 'card' | 'cash'

  // Check auth status on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsLoggedIn(true);
        setStep('confirm_arrival');
        
        // Pre-fill form with user data
        setFormData({
          first_name: parsedUser.first_name || '',
          last_name: parsedUser.last_name || '',
          date_of_birth: parsedUser.date_of_birth || ''
        });
      } catch (e) {
        setIsLoggedIn(false);
        setStep('login_required');
      }
    } else {
      setIsLoggedIn(false);
      setStep('login_required');
    }
  }, []);

  // Check if returning from Stripe payment
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const appointmentId = searchParams.get('appointment_id');
    const errorParam = searchParams.get('error');
    
    if (sessionId && appointmentId) {
      // Returning from successful payment
      confirmPayment(sessionId, appointmentId);
    } else if (errorParam) {
      setStep('error');
      setError('Pagamento annullato. Puoi riprovare.');
    }
  }, [searchParams]);

  const confirmPayment = async (sessionId, appointmentId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkin/payment-complete`, {
        session_id: sessionId,
        appointment_id: appointmentId
      });
      
      setCheckInResult(res.data);
      setStep('success');
      toast.success('Check-in completato! Pagamento ricevuto.');
    } catch (error) {
      console.error('Error confirming payment:', error);
      setError('Errore nella conferma del pagamento. Contatta la reception.');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  // Redirect to login
  const handleLoginRedirect = () => {
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    navigate(`/login?return_url=${returnUrl}`);
  };

  // Confirm arrival and find appointment
  const handleConfirmArrival = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/checkin/identify`, {
        first_name: formData.first_name || user?.first_name,
        last_name: formData.last_name || user?.last_name,
        date_of_birth: formData.date_of_birth || user?.date_of_birth
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (res.data.already_checked_in) {
        setCheckInResult(res.data);
        setStep('success');
        toast.success('Check-in già effettuato!');
      } else if (res.data.amount_to_pay === 0) {
        // Premium subscription - no payment needed
        setCheckInResult(res.data);
        setStep('success');
        toast.success('Check-in completato!');
      } else {
        // Payment required - show payment choice
        setCheckInResult(res.data);
        setStep('payment_choice');
      }
    } catch (error) {
      console.error('Error during check-in:', error);
      setError(error.response?.data?.detail || 'Errore durante il check-in. Verifica i dati inseriti.');
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  // Handle payment method choice
  const handlePaymentChoice = async (method) => {
    setPaymentMethod(method);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (method === 'card') {
        // Request Stripe payment link
        const res = await axios.post(`${API}/checkin/request-payment`, {
          appointment_id: checkInResult.appointment_id
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        setCheckInResult(prev => ({ ...prev, ...res.data }));
        setStep('payment_sent');
        toast.success('Link pagamento inviato via SMS!');
      } else {
        // Cash payment - notify admin for manual check-in
        await axios.post(`${API}/checkin/request-cash`, {
          appointment_id: checkInResult.appointment_id,
          patient_name: checkInResult.patient_name,
          amount: checkInResult.amount_to_pay
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        setStep('waiting_cash');
        toast.success('Reception notificata. Attendi alla cassa.');
      }
    } catch (error) {
      console.error('Error processing payment choice:', error);
      toast.error('Errore nella richiesta. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(180deg, #e8f4fd 0%, #FFFFFF 100%)'}}>
      <Navbar />
      
      <div className="max-w-lg mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #1a6b8a 100%)'}}>
            <User className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold" style={{color: '#2A9FCB'}}>Check-in</h1>
          <p className="text-gray-600 mt-2">Benvenuto da PRIME VISION</p>
        </div>

        {/* Loading */}
        {step === 'loading' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#2A9FCB] mx-auto" />
            <p className="text-gray-600 mt-4">Caricamento...</p>
          </div>
        )}

        {/* Step: Login Required */}
        {step === 'login_required' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center" data-testid="checkin-login-required">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-amber-100">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Accesso Richiesto</h2>
            <p className="text-gray-600 mb-6">
              Per effettuare il check-in, devi prima accedere al tuo account PRIME VISION.
            </p>
            
            <button
              onClick={handleLoginRedirect}
              className="w-full py-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-all hover:opacity-90"
              style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #1a6b8a 100%)'}}
              data-testid="btn-login-redirect"
            >
              Accedi al tuo Account
              <ArrowRight className="w-5 h-5" />
            </button>
            
            <p className="text-sm text-gray-500 mt-4">
              Non hai un account? <a href="/auth?register=true" className="text-[#2A9FCB] hover:underline">Registrati</a>
            </p>
          </div>
        )}

        {/* Step: Confirm Arrival */}
        {step === 'confirm_arrival' && (
          <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="checkin-confirm-arrival">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-100">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Ciao, {user?.first_name || formData.first_name}!</h2>
              <p className="text-gray-600 mt-1">Conferma il tuo arrivo in clinica</p>
            </div>
            
            {/* Se necessario inserire dati diversi (es. minore) */}
            <div className="mb-6 p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-600 mb-3">
                <strong>Stai effettuando il check-in per un'altra persona?</strong><br/>
                Inserisci i dati del paziente (es. figlio minore).
              </p>
              
              <div className="space-y-3">
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                  placeholder="Nome paziente"
                />
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                  placeholder="Cognome paziente"
                />
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                />
              </div>
            </div>
            
            {error && (
              <div className="p-4 bg-red-50 text-red-700 rounded-xl flex items-start gap-3 mb-4">
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}
            
            <button
              onClick={handleConfirmArrival}
              disabled={loading}
              className="w-full py-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50"
              style={{background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)'}}
              data-testid="btn-confirm-arrival"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Conferma il tuo Arrivo
                </>
              )}
            </button>
          </div>
        )}

        {/* Step: Payment Choice */}
        {step === 'payment_choice' && checkInResult && (
          <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="checkin-payment-choice">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Come preferisci pagare?</h2>
              <p className="text-gray-600 mt-1">Importo da pagare: <strong className="text-[#2A9FCB]">€{checkInResult.amount_to_pay?.toFixed(2)}</strong></p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 text-gray-700 mb-2">
                <User className="w-4 h-4" />
                <span className="font-medium">{checkInResult.patient_name}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Clock className="w-4 h-4" />
                <span>Appuntamento: {checkInResult.appointment_time}</span>
              </div>
            </div>
            
            <div className="space-y-3">
              {/* Pagamento con Carta */}
              <button
                onClick={() => handlePaymentChoice('card')}
                disabled={loading}
                className="w-full py-4 rounded-xl text-white font-semibold flex items-center justify-center gap-3 transition-all hover:opacity-90 disabled:opacity-50"
                style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #1a6b8a 100%)'}}
                data-testid="btn-pay-card"
              >
                {loading && paymentMethod === 'card' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    Paga con Carta (Online)
                  </>
                )}
              </button>
              
              {/* Pagamento in Contanti */}
              <button
                onClick={() => handlePaymentChoice('cash')}
                disabled={loading}
                className="w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-3 transition-all hover:opacity-90 disabled:opacity-50 border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
                data-testid="btn-pay-cash"
              >
                {loading && paymentMethod === 'cash' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <span className="text-xl">💵</span>
                    Pago in Contanti alla Cassa
                  </>
                )}
              </button>
            </div>
            
            <p className="text-xs text-gray-500 text-center mt-4">
              Il pagamento con carta è più veloce e ti permette di accedere subito alla sala d'attesa.
            </p>
          </div>
        )}

        {/* Step: Waiting for Cash Payment (Manual Check-in) */}
        {step === 'waiting_cash' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center" data-testid="checkin-waiting-cash">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-amber-100">
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Attendi alla Cassa</h2>
            <p className="text-gray-600 mb-6">
              La reception è stata notificata.<br/>
              Presentati alla cassa per completare il pagamento in contanti.
            </p>
            
            <div className="bg-amber-50 rounded-xl p-4 mb-6 text-left">
              <div className="flex items-center gap-2 text-amber-800 mb-2">
                <User className="w-4 h-4" />
                <span className="font-medium">{checkInResult?.patient_name}</span>
              </div>
              <div className="flex items-center gap-2 text-amber-800">
                <span className="text-xl">💵</span>
                <span className="font-semibold">Da pagare: €{checkInResult?.amount_to_pay?.toFixed(2)}</span>
              </div>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-xl text-blue-800 text-sm">
              <p>Dopo il pagamento, l'operatore completerà il check-in e riceverai conferma.</p>
            </div>
          </div>
        )}

        {/* Step: Payment SMS Sent */}
        {step === 'payment_sent' && checkInResult && (
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center" data-testid="checkin-payment-sent">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-blue-100">
              <Phone className="w-8 h-8 text-blue-600" />
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mb-2">SMS Inviato!</h2>
            <p className="text-gray-600 mb-6">
              Abbiamo inviato un SMS con il link per il pagamento al tuo numero di telefono.
            </p>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
              <div className="flex items-center gap-2 text-gray-700 mb-2">
                <User className="w-4 h-4" />
                <span className="font-medium">{checkInResult.patient_name}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700 mb-2">
                <Clock className="w-4 h-4" />
                <span>Appuntamento: {checkInResult.appointment_time}</span>
              </div>
              {checkInResult.doctor_name && (
                <div className="flex items-center gap-2 text-gray-700 mb-2">
                  <User className="w-4 h-4" />
                  <span>{checkInResult.doctor_name}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-700">
                <CreditCard className="w-4 h-4" />
                <span className="font-semibold">Da pagare: €{checkInResult.amount_to_pay?.toFixed(2)}</span>
              </div>
            </div>
            
            <div className="p-4 bg-yellow-50 rounded-xl text-yellow-800 text-sm mb-6">
              <p>📱 Controlla il tuo telefono e clicca sul link per completare il pagamento.</p>
              <p className="mt-2">Dopo il pagamento, il check-in sarà completato automaticamente.</p>
            </div>
            
            {checkInResult.payment_url && (
              <a
                href={checkInResult.payment_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-4 rounded-xl text-white font-semibold text-center transition-all hover:opacity-90"
                style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #1a6b8a 100%)'}}
                data-testid="btn-pay-now"
              >
                <CreditCard className="w-5 h-5 inline mr-2" />
                Paga Ora
              </a>
            )}
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center" data-testid="checkin-success">
            <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-100">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-green-600 mb-2">Check-in Completato!</h2>
            <p className="text-gray-600 mb-6">
              Ora puoi accomodarti in sala d'attesa.<br />
              Riceverai un SMS quando sarà il tuo turno.
            </p>
            
            {checkInResult && (
              <div className="bg-green-50 rounded-xl p-4 text-left mb-6">
                <div className="flex items-center gap-2 text-gray-700 mb-2">
                  <User className="w-4 h-4" />
                  <span className="font-medium">{checkInResult.patient_name}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700 mb-2">
                  <Clock className="w-4 h-4" />
                  <span>Appuntamento: {checkInResult.appointment_time}</span>
                </div>
                {checkInResult.doctor_name && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <User className="w-4 h-4" />
                    <span>{checkInResult.doctor_name}</span>
                  </div>
                )}
              </div>
            )}
            
            <div className="p-4 bg-blue-50 rounded-xl text-blue-800 text-sm">
              <p>📱 Riceverai un <strong>SMS</strong> e una <strong>notifica</strong> quando il medico ti chiamerà con l'indicazione della stanza.</p>
            </div>
          </div>
        )}

        {/* Step: Error */}
        {step === 'error' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center" data-testid="checkin-error">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-red-100">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            
            <h2 className="text-xl font-semibold text-red-600 mb-2">Ops!</h2>
            <p className="text-gray-600 mb-6">{error || 'Si è verificato un errore.'}</p>
            
            <button
              onClick={() => {
                setStep('identify');
                setError(null);
                setFormData({ first_name: '', last_name: '', date_of_birth: '' });
              }}
              className="w-full py-4 rounded-xl text-white font-semibold transition-all hover:opacity-90"
              style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #1a6b8a 100%)'}}
            >
              Riprova
            </button>
          </div>
        )}

        {/* Loading overlay */}
        {loading && step !== 'identify' && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 text-center">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{color: '#2A9FCB'}} />
              <p className="text-gray-600">Elaborazione in corso...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckIn;
